import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Check, Shield, Zap, Sparkles, Clock, CircleAlert, Plus, HelpCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScheduleConsultationSection from '../components/ScheduleConsultationSection';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { managedServicesTiers } from '../data/managedServices';

const iconMap = { Shield, Zap, Sparkles };

const sevStyles = {
  Critical: 'text-red-300 bg-red-500/10 border-red-500/30',
  Major: 'text-orange-300 bg-orange-500/10 border-orange-500/30',
  Moderate: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  Minor: 'text-slate-300 bg-white/5 border-white/10',
};

const ManagedServicesPage = () => {
  const [activeTier, setActiveTier] = useState('advance');
  const activeTierData = managedServicesTiers.find((t) => t.id === activeTier);

  return (
    <main className="bg-[#05080f] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 md:pt-44 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.15),transparent_60%)]"></div>
        <div className="perspective-grid"></div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">Managed Services</div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-white max-w-4xl">
            Elevating cloud operations through <span className="gradient-text">intelligent monitoring</span>,
            security, and continuous optimization
          </h1>
          <p className="mt-6 text-slate-400 max-w-2xl text-base md:text-lg">
            Three purpose-built tiers to match your infrastructure size and criticality — from foundational
            monitoring for lean teams to AI-driven enterprise operations for mission-critical estates.
          </p>
        </div>
      </section>

      {/* Tier Cards */}
      <section className="relative py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-6">
            {managedServicesTiers.map((tier) => {
              const Icon = iconMap[tier.icon] || Shield;
              return (
                <div
                  key={tier.id}
                  className={`relative rounded-3xl p-8 flex flex-col transition-all duration-300 ${
                    tier.highlight
                      ? 'border border-amber-500/40 bg-gradient-to-b from-[#1a1408]/80 to-[#0b0906]/80 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.35)]'
                      : 'card-dark hover:border-amber-500/25'
                  }`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="btn-gradient text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
                        Most popular
                      </span>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-5">
                    <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                      <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                    </div>
                    <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-slate-300">
                      {tier.subtitle}
                    </span>
                  </div>

                  <h3 className="font-display text-2xl font-semibold text-white">{tier.name}</h3>
                  <p className="text-amber-300 text-sm mt-1">{tier.tagline}</p>
                  <p className="text-slate-400 text-sm mt-4 leading-relaxed">{tier.description}</p>

                  <div className="mt-4 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/5">
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide mb-0.5">Ideal for</div>
                    <div className="text-slate-300 text-sm">{tier.idealFor}</div>
                  </div>

                  <div className="mt-5 flex items-center gap-2 text-sm text-slate-300">
                    <Clock size={14} className="text-amber-400" />
                    {tier.support}
                  </div>

                  <ul className="mt-5 space-y-2.5 flex-1">
                    {tier.features.slice(0, 8).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-amber-400 mt-1 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                    {tier.features.length > 8 && (
                      <li className="text-xs text-slate-500 pl-6">
                        + {tier.features.length - 8} more capabilities
                      </li>
                    )}
                  </ul>

                  <div className="mt-6 pt-5 border-t border-white/5 flex flex-col gap-3">
                    <button
                      onClick={() => { setActiveTier(tier.id); document.getElementById('sla-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="text-amber-300 hover:text-amber-200 text-sm inline-flex items-center gap-1.5"
                    >
                      View full details & SLAs <ArrowUpRight size={13} />
                    </button>
                    <Link to="/#contact">
                      <Button className={`w-full rounded-full h-11 border-0 ${
                        tier.highlight ? 'btn-gradient' : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white'
                      }`}>
                        Contact for pricing
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed comparison + SLAs */}
      <section id="sla-section" className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Service Level Agreements</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
            Transparent commitments on <span className="gradient-text">response & resolution</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mb-10">
            Every tier includes clearly defined SLAs for both service requests and incidents across four severity levels.
            Pick the tier below to see full details.
          </p>

          <Tabs value={activeTier} onValueChange={setActiveTier}>
            <TabsList className="bg-white/[0.03] border border-white/10 p-1 rounded-full">
              {managedServicesTiers.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="rounded-full px-5 py-2 text-sm text-slate-300 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-200"
                >
                  {t.name.replace('Pratham ', '')}
                </TabsTrigger>
              ))}
            </TabsList>

            {managedServicesTiers.map((tier) => (
              <TabsContent key={tier.id} value={tier.id} className="mt-8">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* All features */}
                  <div className="card-dark rounded-3xl p-7">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles size={16} className="text-amber-400" />
                      <h3 className="text-white font-semibold text-lg">Everything included</h3>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check size={14} className="text-amber-400 mt-1 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {tier.addons?.length > 0 && (
                      <div className="mt-6 pt-5 border-t border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <Plus size={14} className="text-amber-400" />
                          <span className="text-white font-medium text-sm">Available add-ons</span>
                          <span className="text-xs text-slate-500">(on request)</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tier.addons.map((a) => (
                            <span key={a} className="text-xs px-3 py-1 rounded-full bg-amber-500/8 text-amber-200 border border-amber-500/25">
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SLAs */}
                  <div className="space-y-5">
                    <SLATable
                      title="Service Request SLA"
                      subtitle={tier.support}
                      rows={tier.srSLA}
                      icon={<HelpCircle size={16} className="text-amber-400" />}
                    />
                    <SLATable
                      title="Incident SLA"
                      subtitle="Response → Resolution targets"
                      rows={tier.incidentSLA}
                      icon={<CircleAlert size={16} className="text-amber-400" />}
                    />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Schedule consultation */}
      <ScheduleConsultationSection
        label="Schedule a Call"
        title={<>Talk to a <span className="gradient-text">managed services expert</span></>}
        subtitle="Pick a date to speak with our team about the right tier for your infrastructure. We'll follow up to confirm."
      />

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12),transparent_60%)]"></div>
            <div className="relative">
              <h2 className="font-display text-2xl md:text-4xl font-semibold text-white tracking-tight mb-4">
                Not sure which tier fits you?
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto mb-8">
                Our team will assess your workloads, environments, and criticality to recommend the right
                managed services package — no obligation.
              </p>
              <Link to="/#contact">
                <Button className="btn-gradient rounded-full h-11 px-8 border-0">
                  Get a personalized recommendation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

const SLATable = ({ title, subtitle, rows, icon }) => (
  <div className="card-dark rounded-3xl p-6">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <h4 className="text-white font-semibold">{title}</h4>
    </div>
    <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 text-sm">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Severity</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide text-right">Response</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide text-right">Resolution</div>
      {rows.map((r) => (
        <React.Fragment key={r.sev}>
          <div>
            <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${sevStyles[r.sev]}`}>{r.sev}</span>
          </div>
          <div className="text-slate-300 text-right font-medium">{r.response}</div>
          <div className="text-amber-300 text-right font-medium">{r.resolution}</div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export default ManagedServicesPage;
